import { DocumentSection, EntityDocument } from '@toggle/toggle';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatInput } from '~/components/chat';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TrackingMetadata } from '~/services/tracking/posthog/constants';
import { DocumentViewer } from '~/views/alfred/document-search/document-viewer/DocumentViewer';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import * as S from './CitationsAccordionWidget.styles';
import { Citations } from './components/citations/Citations';

export interface CitationsAccordionWidgetProps {
  citations: DocumentSection[];
  trackingMetadata?: TrackingMetadata;
}

export const CitationsAccordionWidget = ({
  citations,
  trackingMetadata,
}: CitationsAccordionWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToAlfred } = useChatNavigation();
  const citationsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<EntityDocument>();
  const [activeCitationIdx, setActiveCitationIdx] = useState<number>();
  const [isChatInputOpen, setIsChatInputOpen] = useState(false);

  const onClickCitation = (citationId: string, document: EntityDocument) => {
    const citationIdx = citations.findIndex(c => c.id === citationId);

    if (citationIdx === -1) {
      setActiveCitationIdx(undefined);
      setSelectedDoc(undefined);
      return;
    }

    setActiveCitationIdx(citationIdx);
    setSelectedDoc(document);
  };

  const onClose = () => {
    setSelectedDoc(undefined);
    setActiveCitationIdx(undefined);
  };

  const items = [
    {
      title: t('widget:citations.label'),
      content: (
        <ErrorBoundaryWrapper>
          <Citations
            citations={citations}
            onClick={(citationId, doc) => onClickCitation(citationId, doc)}
          />
        </ErrorBoundaryWrapper>
      ),
    },
  ];

  const getChatProps = () => ({
    chatContext: {
      documents: [selectedDoc?.id] as string[],
    },
    config: {
      allowedModes: [],
      clearDocumentContextOnSubmit: true,
      disableContextHeader: true,
      persistInputTextOnSubmit: true,
      placeholder: t('widget:citations.askQuestionAbout', {
        documentName: selectedDoc?.name,
      }),
    },
    submitCallback: () =>
      navigateToAlfred('document', {
        trackingSource: trackingMetadata?.source,
      }),
    trackingMetadata,
  });

  const page =
    activeCitationIdx !== undefined
      ? citations[activeCitationIdx]?.tags?.page
      : null;

  const getHighlightTexts = () => {
    const citation =
      activeCitationIdx !== undefined ? citations[activeCitationIdx] : null;

    if (!selectedDoc || selectedDoc.kind === 'presentation') {
      return [];
    }

    return [
      {
        text: citation
          ? citation.content
              .trim()
              .replace(/(\*\*)(.*?)\1/g, '$2')
              .replace(/\s+/g, ' ')
          : '',
        pages: page ? [parseInt(page)] : undefined,
        isPrimary: true,
      },
    ];
  };

  if (!citations.length) {
    return null;
  }

  return (
    <>
      <S.StyledAccordion items={items} />
      {!!selectedDoc && (
        <S.StyledDrawer isOpen handleCloseClick={onClose}>
          <ErrorBoundaryWrapper>
            <S.DrawerSectionWrapper>
              <S.DrawerSection>
                <S.CitationsHeaderTitle>
                  {t(
                    'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.citations'
                  )}
                </S.CitationsHeaderTitle>
                <Citations
                  citations={citations}
                  onClick={onClickCitation}
                  activeIdx={activeCitationIdx}
                  ref={citationsRefs}
                />
              </S.DrawerSection>
              <S.DocumentChatButton
                iconName="Alfred"
                label={t('widget:citations.documentChat')}
                onClick={() => setIsChatInputOpen(true)}
              />
              <S.DrawerSection>
                {!!selectedDoc && (
                  <DocumentViewer
                    highlightTexts={getHighlightTexts()}
                    selectedCitation={{
                      document_id: selectedDoc.id,
                      document_name: selectedDoc.name,
                      document_type: selectedDoc.type,
                      pages: page ? [parseInt(page)] : [],
                    }}
                  />
                )}
              </S.DrawerSection>
            </S.DrawerSectionWrapper>
          </ErrorBoundaryWrapper>
        </S.StyledDrawer>
      )}
      {selectedDoc && isChatInputOpen && (
        <S.DocumentChatModal
          isOpen={isChatInputOpen}
          onClose={() => setIsChatInputOpen(false)}
          title={t('widget:citations.documentChat')}
          description={<ChatInput {...getChatProps()} />}
        />
      )}
    </>
  );
};
